import { post } from "aws-amplify/api";

/**
 * Thin wrapper for the ML API — the only backend this app talks to.
 *
 * Every route is POST with a `meth` key naming the operation. R&D pages each own
 * one endpoint under `/v1/frontend/<page>`; see `config.mlApi.frontendPath`.
 *
 * SECURITY: this uses Amplify's REST `post`, which SigV4-signs every request
 * with the Cognito Identity Pool (IAM) credentials configured in `index.tsx`.
 * Keep calls going through this helper — do NOT swap in a raw `fetch`, which
 * would drop the signing and get a 403 from API Gateway.
 *
 * Per the repo server-state rules this layer shows no UI feedback: it posts and
 * returns the parsed JSON body. Callers validate the shape (e.g. with Zod) and
 * own any user-facing messaging. Access control is enforced at the route level
 * (`AuthGate` + `AppFeatureGate`) and on the backend.
 */

const ML_API_NAME = "ML_API";

/** The JSON body type Amplify's REST `post` accepts, derived from its signature. */
export type MlApiRequestBody = NonNullable<
  NonNullable<Parameters<typeof post>[0]["options"]>["body"]
>;

/**
 * Bare `Infinity`, `-Infinity` and `NaN` used as JSON values.
 *
 * Python's `json.dumps` emits these for non-finite floats. They are not valid
 * JSON, and `JSON.parse` rejects the whole document over one of them — so a
 * single `Infinity` in one event's metadata would blank a page of five thousand
 * good rows.
 *
 * The preceding structural character is captured and restored rather than
 * matched with a lookbehind, which Safari only gained recently. A token inside a
 * quoted string could match in principle; in practice these appear where the
 * backend computed a ratio with a zero denominator.
 */
const NON_FINITE_JSON_VALUE = /([:,[\s])-?(?:Infinity|NaN)(?=[\s,\]}])/g;

/**
 * Parse an ML API response body, reading non-finite numbers as null — the honest
 * value for "the backend couldn't compute this", and one every schema here
 * already tolerates.
 */
function parseMlApiJson(text: string): unknown {
  if (text.trim() === "") {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    const repaired = text.replace(NON_FINITE_JSON_VALUE, "$1null");

    // Rethrow the original error if the repair changed nothing — the body is
    // malformed for some other reason, and that's the more useful message.
    if (repaired === text) {
      throw error;
    }

    return JSON.parse(repaired);
  }
}

export async function postMlApi(
  path: string,
  body?: MlApiRequestBody,
): Promise<unknown> {
  const { body: responseBody } = await post({
    apiName: ML_API_NAME,
    path,
    options: body === undefined ? undefined : { body },
  }).response;

  // Read as text and parse here rather than calling `responseBody.json()`: the
  // repair above needs the raw body, and a failed `json()` has already consumed
  // the stream.
  return parseMlApiJson(await responseBody.text());
}

/**
 * HTTP status behind an Amplify REST failure, when there is one. Network faults
 * have none.
 */
export function mlApiErrorStatus(error: unknown): number | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const { response, $metadata } = error as {
    response?: { statusCode?: unknown };
    $metadata?: { httpStatusCode?: unknown };
  };
  const status = response?.statusCode ?? $metadata?.httpStatusCode;

  return typeof status === "number" ? status : undefined;
}

/**
 * The ML API's own message from an error response body (`{"error": "..."}`).
 *
 * Worth surfacing: Amplify reports these as a generic `UnknownError` because the
 * body isn't in the AWS error shape, so without this the reason a request was
 * rejected — a bad param name, an unaccepted enum value — never reaches the UI.
 */
export function mlApiErrorMessage(error: unknown): string | undefined {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const { response } = error as { response?: { body?: unknown } };

  if (typeof response?.body !== "string") {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(response.body);
    const message = (parsed as { error?: unknown } | null)?.error;

    return typeof message === "string" ? message : undefined;
  } catch {
    return undefined;
  }
}
